import { useState, useRef, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";

interface HorizontalNFTScrollProps<T> {
  items: T[];
  maxVisible?: number;
  renderItem: (item: T, index: number) => React.ReactNode;
  emptyState?: React.ReactNode;
}

export function HorizontalNFTScroll<T extends { id: string }>({
  items,
  maxVisible = 6,
  renderItem,
  emptyState,
}: HorizontalNFTScrollProps<T>) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const displayItems = items.slice(0, maxVisible);
  const totalItems = displayItems.length;

  const updateScrollState = () => {
    if (!scrollRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 10);
    
    // Calculate current index based on scroll position
    const itemWidth = scrollRef.current.scrollWidth / totalItems;
    const newIndex = Math.round(scrollLeft / itemWidth);
    setCurrentIndex(Math.min(newIndex, totalItems - 1));
  };

  useEffect(() => {
    updateScrollState();
    const scrollEl = scrollRef.current;
    if (scrollEl) {
      scrollEl.addEventListener("scroll", updateScrollState);
      return () => scrollEl.removeEventListener("scroll", updateScrollState);
    }
  }, [totalItems]);

  const scrollToIndex = (index: number) => {
    if (!scrollRef.current || totalItems === 0) return;
    const itemWidth = scrollRef.current.scrollWidth / totalItems;
    scrollRef.current.scrollTo({
      left: index * itemWidth,
      behavior: "smooth",
    });
  };

  const scrollLeft = () => {
    const newIndex = Math.max(0, currentIndex - 1);
    scrollToIndex(newIndex);
  };

  const scrollRight = () => {
    const newIndex = Math.min(totalItems - 1, currentIndex + 1);
    scrollToIndex(newIndex);
  };

  if (items.length === 0) {
    return <>{emptyState}</>;
  }

  return (
    <div className="space-y-3">
      {/* Horizontal Scroll Container */}
      <div className="relative">
        {/* Left Arrow */}
        {canScrollLeft && (
          <Button
            variant="outline"
            size="icon"
            onClick={scrollLeft}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 h-8 w-8 bg-background/90 backdrop-blur-sm shadow-md"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
        )}

        {/* Scrollable Area */}
        <div
          ref={scrollRef}
          className="flex gap-4 overflow-x-auto scrollbar-hide scroll-smooth pb-2"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
        >
          {displayItems.map((item, index) => (
            <div
              key={item.id}
              className="flex-shrink-0 w-[calc(50%-8px)] sm:w-[calc(33.333%-11px)] lg:w-[calc(33.333%-11px)]"
            >
              {renderItem(item, index)}
            </div>
          ))}
        </div>

        {/* Right Arrow */}
        {canScrollRight && (
          <Button
            variant="outline"
            size="icon"
            onClick={scrollRight}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 h-8 w-8 bg-background/90 backdrop-blur-sm shadow-md"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Numeric Indicator */}
      {totalItems > 1 && (
        <div className="flex items-center justify-center gap-2">
          <div className="flex items-center gap-1">
            {displayItems.map((_, index) => (
              <button
                key={index}
                onClick={() => scrollToIndex(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentIndex
                    ? "bg-primary w-4"
                    : "bg-muted-foreground/30 hover:bg-muted-foreground/50"
                }`}
                aria-label={`Go to item ${index + 1}`}
              />
            ))}
          </div>
          <span className="text-xs text-muted-foreground">
            {currentIndex + 1} / {totalItems}
          </span>
        </div>
      )}
    </div>
  );
}
